import { z } from 'zod';
import { AuditedFields, CursorPageQuery, Email, IsoDate, IsoDateTime, PhoneE164, Uuid, Version, } from './common.js';
import { Address } from './tenant.js';
/** Personne physique ou personne morale (D-005). */
export const CustomerKind = z.enum(['PERSON', 'COMPANY']);
export const Gender = z.enum(['M', 'F', 'OTHER', 'UNSPECIFIED']);
/** Type de pièce d'identité acceptée (D-013 : CNIB ou passeport pour la conformité). */
export const IdDocumentType = z.enum([
    'CNIB',
    'PASSPORT',
    'RESIDENCE_PERMIT',
    'DRIVING_LICENSE',
    'OTHER',
]);
export const CustomerSource = z.enum([
    'DIRECT',
    'PHONE',
    'WHATSAPP',
    'EMAIL',
    'WEBSITE',
    'BOOKING_COM',
    'AIRBNB',
    'CORPORATE',
    'REFERRAL',
    'OTHER',
]);
export const PersonDetails = z.object({
    lastName: z.string().min(1).max(80),
    firstNames: z.string().min(1).max(120),
    gender: Gender.nullable(),
    birthDate: IsoDate.nullable(),
    nationalityCode: z.string().length(2).nullable(),
    profession: z.string().max(120).nullable(),
    idDocumentType: IdDocumentType.nullable(),
    /** Numéro de pièce : accès restreint (document.view_identity) ; masqué dans les listes. */
    idDocumentNumber: z.string().max(60).nullable(),
    idDocumentExpiresAt: IsoDate.nullable(),
    idDocumentIssuingCountry: z.string().length(2).nullable(),
});
export const CompanyDetails = z.object({
    legalName: z.string().min(1).max(160),
    tradeName: z.string().max(160).nullable(),
    legalForm: z.string().max(40).nullable(),
    ifu: z.string().max(30).nullable(),
    rccm: z.string().max(60).nullable(),
    taxRegime: z.string().max(40).nullable(),
    taxDivision: z.string().max(60).nullable(),
    billingContactName: z.string().max(120).nullable(),
    billingEmail: Email.nullable(),
    /** Proposer la retenue à la source 5 % par défaut sur les documents de ce client (D-012). */
    withholdingDefault: z.boolean(),
    taxExempt: z.boolean(),
});
export const MembershipRole = z.enum(['EMPLOYEE', 'MANAGER', 'BILLING_CONTACT', 'OTHER']);
export const CompanyMembership = z.object({
    id: Uuid,
    personId: Uuid,
    personName: z.string(),
    companyId: Uuid,
    companyName: z.string(),
    role: MembershipRole,
    isPrimary: z.boolean(),
    validFrom: IsoDate.nullable(),
    validTo: IsoDate.nullable(),
});
export const CustomerSummary = z.object({
    id: Uuid,
    kind: CustomerKind,
    displayName: z.string(),
    email: Email.nullable(),
    phonePrimary: PhoneE164.nullable(),
    /** Entreprise principale d'une personne (pour préremplir « facturé à »). */
    primaryCompany: z.object({ id: Uuid, displayName: z.string() }).nullable(),
});
export const Customer = z
    .object({
    id: Uuid,
    kind: CustomerKind,
    displayName: z.string(),
    email: Email.nullable(),
    phonePrimary: PhoneE164.nullable(),
    phoneSecondary: PhoneE164.nullable(),
    address: Address.nullable(),
    notes: z.string().nullable(),
    tags: z.array(z.string()),
    source: CustomerSource.nullable(),
    person: PersonDetails.nullable(),
    company: CompanyDetails.nullable(),
    memberships: z.array(CompanyMembership),
    stats: z.object({
        reservationsCount: z.number().int(),
        lastStayAt: IsoDateTime.nullable(),
        balance: z.number(),
    }),
    isArchived: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
const CustomerBase = z.object({
    email: Email.nullable().optional(),
    phonePrimary: PhoneE164.nullable().optional(),
    phoneSecondary: PhoneE164.nullable().optional(),
    address: Address.nullable().optional(),
    notes: z.string().max(4000).nullable().optional(),
    tags: z.array(z.string().max(40)).optional(),
    source: CustomerSource.nullable().optional(),
});
export const CreatePersonRequest = CustomerBase.extend({
    kind: z.literal('PERSON'),
    person: PersonDetails.partial({
        gender: true,
        birthDate: true,
        nationalityCode: true,
        profession: true,
        idDocumentType: true,
        idDocumentNumber: true,
        idDocumentExpiresAt: true,
        idDocumentIssuingCountry: true,
    }),
    /** Rattachement immédiat à une entreprise (création inline depuis une réservation). */
    companyId: Uuid.nullable().optional(),
    /** Rôle du rattachement créé avec `companyId` ; ignoré sans `companyId`. */
    companyRole: MembershipRole.default('EMPLOYEE'),
    /**
     * Le rattachement créé avec `companyId` devient l'entreprise principale de la
     * personne (c'est elle qui préremplit « facturé à »). Ignoré sans `companyId`.
     */
    companyIsPrimary: z.boolean().default(true),
});
export const CreateCompanyRequest = CustomerBase.extend({
    kind: z.literal('COMPANY'),
    company: CompanyDetails.partial({
        tradeName: true,
        legalForm: true,
        ifu: true,
        rccm: true,
        taxRegime: true,
        taxDivision: true,
        billingContactName: true,
        billingEmail: true,
        withholdingDefault: true,
        taxExempt: true,
    }),
});
export const CreateCustomerRequest = z.discriminatedUnion('kind', [
    CreatePersonRequest,
    CreateCompanyRequest,
]);
export const UpdateCustomerRequest = CustomerBase.extend({
    person: PersonDetails.partial().optional(),
    company: CompanyDetails.partial().optional(),
    isArchived: z.boolean().optional(),
    version: Version,
});
export const ListCustomersQuery = CursorPageQuery.extend({
    kind: CustomerKind.optional(),
    q: z.string().max(100).optional(),
    companyId: Uuid.optional(),
    includeArchived: z.coerce.boolean().default(false),
});
export const CreateMembershipRequest = z.object({
    personId: Uuid,
    companyId: Uuid,
    role: MembershipRole.default('EMPLOYEE'),
    isPrimary: z.boolean().default(false),
    validFrom: IsoDate.nullable().optional(),
    validTo: IsoDate.nullable().optional(),
});
export const UpdateMembershipRequest = CreateMembershipRequest.omit({
    personId: true,
    companyId: true,
}).partial();
/** Recherche instantanée (autocomplete) pour la création de réservation. */
export const SearchCustomersQuery = z.object({
    q: z.string().min(1).max(100),
    kind: CustomerKind.optional(),
    limit: z.coerce.number().int().min(1).max(20).default(8),
});
/**
 * Archivage / restauration d'un client (corps facultatif, comme pour les
 * ressources) : sans `version`, le serveur agit sur la version qu'il vient de
 * lire ; avec, le verrou optimiste s'applique.
 */
export const ArchiveCustomerRequest = z.object({
    version: Version.optional(),
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=customers.js.map